import React, { useState } from 'react';
import { accountsApi } from '../accounts/api/accounts';
import { contactsApi } from '../contacts/api/contacts';
import { getIndustryOptions } from '../../config/industry-master';
import { getDivisionOptions } from '../../config/divisions';

const EMPTY_FORM = {
  Name: '',
  Industry: '',
  Division: 'LOCAL',
  Location: '',
  ContactName: '',
  Designation: '',
  Email1: '',
  Phone1: '',
};

const AddCustomerModal = ({ isOpen, onClose, onCustomerAdded }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dupMatches, setDupMatches] = useState([]);

  const industryOptions = getIndustryOptions();
  const divisionOptions = getDivisionOptions();

  const reset = () => {
    setFormData(EMPTY_FORM);
    setError(null);
    setDupMatches([]);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // The contact is optional: a customer can be created as a company on its own.
  const createContactFor = async (accountId) => {
    if (!formData.ContactName.trim()) return;

    const payload = {
      Name: formData.ContactName.trim(),
      AccountID: parseInt(accountId, 10),
      Designation: formData.Designation.trim(),
      Email1: formData.Email1.trim(),
    };
    if (formData.Phone1.trim()) payload.Phone1 = formData.Phone1.trim();

    await contactsApi.createContact(payload);
  };

  const buildAccountPayload = (force = false) => {
    const payload = {
      Name: formData.Name.trim(),
      Industry: formData.Industry.trim(),
      Division: formData.Division,
    };
    if (formData.Location.trim()) payload.Location = formData.Location.trim();
    if (force) payload.force_create = true;
    return payload;
  };

  const submit = async (force) => {
    setIsSubmitting(true);
    setError(null);

    try {
      if (!formData.Name.trim()) throw new Error('Company name is required');
      if (formData.ContactName.trim() && !formData.Email1.trim()) {
        throw new Error('Contact email is required when adding a contact');
      }

      const account = await accountsApi.createAccount(buildAccountPayload(force));
      await createContactFor(account.id);

      await onCustomerAdded(account.id);
      handleClose();
    } catch (err) {
      if (err.isDuplicate) {
        setDupMatches(err.matches || []);
        setError(err.message);
      } else {
        setError(err.message || 'Failed to create customer');
      }
      setIsSubmitting(false);
    }
  };

  // Attach the new contact to an account that already exists instead of creating a duplicate.
  const attachToExistingAccount = async (accountId) => {
    setIsSubmitting(true);
    setError(null);
    try {
      await createContactFor(accountId);
      await onCustomerAdded(accountId);
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to add contact to the existing customer');
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Add Customer</h2>
            <p className="text-sm text-gray-500">Creates the company and its first contact together</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">×</button>
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); submit(false); }}
          className="px-6 py-5 space-y-6"
        >
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
              {dupMatches.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="font-medium text-red-800">Similar customers already exist:</p>
                  {dupMatches.map((match) => (
                    <div key={match.id} className="flex items-center justify-between bg-white border border-red-200 rounded-lg px-3 py-2">
                      <span className="text-sm text-gray-800">{match.name || match.Name}</span>
                      <button
                        type="button"
                        disabled={isSubmitting}
                        onClick={() => attachToExistingAccount(match.id)}
                        className="text-xs font-medium text-blue-700 hover:text-blue-900 disabled:opacity-50"
                      >
                        Use this one
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    disabled={isSubmitting}
                    onClick={() => submit(true)}
                    className="text-xs font-medium text-red-700 underline hover:text-red-900 disabled:opacity-50"
                  >
                    Create as a new customer anyway
                  </button>
                </div>
              )}
            </div>
          )}

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Company</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                <input
                  name="Name"
                  value={formData.Name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sale Type</label>
                <select
                  name="Division"
                  value={formData.Division}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {divisionOptions.map((option) => (
                    <option key={option.id} value={option.id}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                <select
                  name="Industry"
                  value={formData.Industry}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select industry</option>
                  {industryOptions.map((industry) => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  name="Location"
                  value={formData.Location}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-1">Primary Contact</h3>
            <p className="text-xs text-gray-500 mb-3">Optional — leave blank to add contacts later.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Name</label>
                <input
                  name="ContactName"
                  value={formData.ContactName}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
                <input
                  name="Designation"
                  value={formData.Designation}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email {formData.ContactName.trim() && '*'}
                </label>
                <input
                  type="email"
                  name="Email1"
                  value={formData.Email1}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  name="Phone1"
                  value={formData.Phone1}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !formData.Name.trim()}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isSubmitting ? 'Creating...' : 'Create Customer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddCustomerModal;
